import { ChangeDetectionStrategy, Component, ElementRef, inject, Injector, OnDestroy, OnInit, signal, ViewChild, input, output } from '@angular/core'
import { form } from '@angular/forms/signals'

import { AsiakkaanTuote, TypeaheadTuote } from '@lemon/core-firebase/model/lasku'

import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LaskunTuoteTypeaheadBase } from '../_jaettu-angular/service/lasku/typeahead.service'

const EHDOTUKSIA_ENINTAAN = 7

@Component({
  selector: 'tbody[app-uusi-laskurivi-haku]',
  templateUrl: './uusi-laskurivi-haku.component.html',
  styleUrls: ['./uusi-laskurivi-haku.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UusiLaskuriviHakuComponent implements OnInit, OnDestroy {
  @ViewChild('hakukentta') hakukentta: ElementRef<HTMLInputElement>

  readonly laskunValuutta = input<string | null>(null)
  readonly sarakkeita = input(8)

  readonly rekisterituoteValittiinTapahtuma = output<AsiakkaanTuote>()
  readonly vapaaRiviLisattiinTapahtuma = output<string>()

  readonly hakusana = form(signal(''))
  readonly ehdotukset = signal<TypeaheadTuote[]>([])
  readonly valikkoOnAuki = signal(false)
  readonly aktiivinenIndeksi = signal(0)

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(private _tuoteTypeahead: LaskunTuoteTypeaheadBase) {}

  ngOnInit() {
    arvonMuutokset(this.hakusana, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(arvo => {
        this.avaaValikko()
        this._haeEhdotukset(arvo)
      })
  }

  get hakusanaOnAnnettu(): boolean {
    return !!this.hakusana().value()?.trim()
  }

  kohdistaHakukenttaan() {
    this.hakukentta?.nativeElement.focus()
  }

  avaaValikko() {
    this.valikkoOnAuki.set(true)
  }

  suljeValikkoViiveella() {
    setTimeout(() => {
      this.valikkoOnAuki.set(false)
    }, 150)
  }

  kasitteleHaunNappain(event: KeyboardEvent) {
    const viimeinenIndeksi = this.ehdotukset().length
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.aktiivinenIndeksi.set(Math.min(this.aktiivinenIndeksi() + 1, viimeinenIndeksi))
        break
      case 'ArrowUp':
        event.preventDefault()
        this.aktiivinenIndeksi.set(Math.max(this.aktiivinenIndeksi() - 1, 0))
        break
      case 'Enter':
        event.preventDefault()
        this._valitseAktiivinen()
        break
      case 'Escape':
        this.valikkoOnAuki.set(false)
        break
    }
  }

  async valitseRekisterituote(ehdotus: TypeaheadTuote) {
    const rekisterituote = await this._tuoteTypeahead.getTuote(ehdotus.avain)
    this._tyhjennaHaku()
    if (rekisterituote) {
      this.rekisterituoteValittiinTapahtuma.emit(rekisterituote)
    } else {
      this.vapaaRiviLisattiinTapahtuma.emit(ehdotus.nimi)
    }
  }

  lisaaVapaaRivi() {
    const nimi = this.hakusana().value()?.trim()
    if (!nimi) {
      return
    }
    this._tyhjennaHaku()
    this.vapaaRiviLisattiinTapahtuma.emit(nimi)
  }

  private _valitseAktiivinen() {
    if (!this.hakusanaOnAnnettu) {
      return
    }
    const ehdotus = this.ehdotukset()[this.aktiivinenIndeksi()]
    if (ehdotus) {
      this.valitseRekisterituote(ehdotus)
    } else {
      this.lisaaVapaaRivi()
    }
  }

  private async _haeEhdotukset(arvo: string) {
    const haku = arvo?.trim()
    if (!haku) {
      this.ehdotukset.set([])
      return
    }
    const tulos = await this._tuoteTypeahead.haeJaSuodata(haku, this.laskunValuutta())
    const loytynyt = tulos.loytynyt
    const ehdotukset = loytynyt ? [loytynyt, ...tulos.suodatetut.filter(ehdotus => ehdotus.avain !== loytynyt.avain)] : tulos.suodatetut
    this.ehdotukset.set(ehdotukset.slice(0, EHDOTUKSIA_ENINTAAN))
    this.aktiivinenIndeksi.set(0)
  }

  private _tyhjennaHaku() {
    asetaArvo(this.hakusana, '', { ilmoita: false })
    this.ehdotukset.set([])
    this.valikkoOnAuki.set(false)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
